import { ChevronDown, ChevronRight, Settings } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card, CardContent } from '@/components/ui/card'
import { FleetSettings, TipsHandlingMode } from '@/types'

interface ReviewSettingsPanelProps {
  settings: FleetSettings | null
  isExpanded: boolean
  onToggleExpand: () => void
}

function formatTipsHandling(mode: TipsHandlingMode): string {
  switch (mode) {
    case 'fleet_commission':
      return 'Provision på dricks'
    case 'driver_share':
      return 'Ingen provision på dricks'
    case 'driver_only':
      return 'Dricks endast till förare'
    default:
      return mode
  }
}

function formatTaxMethod(settings: FleetSettings): string {
  if (settings.taxCalculationMethod === 'fixed') {
    return `Fast ${settings.taxRate}%`
  }
  const tableNumber = settings.taxTableName?.replace(/\D/g, '') || '31'
  return `Tabell ${tableNumber}, Kol ${settings.taxColumn}`
}

export function ReviewSettingsPanel({
  settings,
  isExpanded,
  onToggleExpand
}: ReviewSettingsPanelProps) {
  if (!settings) {
    return null
  }

  const summaryText = [
    `Förarandel: ${settings.driverRevenueShare}%`,
    `Moms: ${settings.vatRate}%`,
    `Skatt: ${formatTaxMethod(settings)}`
  ].join(' | ')

  return (
    <Card>
      <button
        onClick={onToggleExpand}
        className={cn(
          'w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors',
          isExpanded && 'border-b'
        )}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
          <Settings className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">Inställningar</span>
        </div>
        {!isExpanded && (
          <span className="text-sm text-muted-foreground">{summaryText}</span>
        )}
      </button>

      {isExpanded && (
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground">Förarandel</p>
              <p className="font-medium">{settings.driverRevenueShare}%</p>
            </div>
            <div>
              <p className="text-muted-foreground">Semesterersättning</p>
              <p className="font-medium">{settings.vacationPayRate}%</p>
            </div>
            <div>
              <p className="text-muted-foreground">Moms</p>
              <p className="font-medium">{settings.vatRate}%</p>
            </div>
            <div>
              <p className="text-muted-foreground">Skattemetod</p>
              <p className="font-medium">{formatTaxMethod(settings)}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Arbetsgivaravgift</p>
              <p className="font-medium">{settings.employerTaxRate}%</p>
            </div>
            <div>
              <p className="text-muted-foreground">Drickshantering</p>
              <p className="font-medium">{formatTipsHandling(settings.feeOnTips)}</p>
            </div>
          </div>
          {settings.revenueShareMode === 'tiered' && settings.revenueShareTiers && (
            <div className="mt-4 pt-4 border-t">
              <p className="text-muted-foreground text-sm mb-2">Stegvis förarandel</p>
              <div className="space-y-1">
                {settings.revenueShareTiers.map((tier, index) => (
                  <div key={index} className="text-sm flex justify-between">
                    <span>
                      {tier.minRevenue.toLocaleString('sv-SE')} kr
                      {tier.maxRevenue ? ` - ${tier.maxRevenue.toLocaleString('sv-SE')} kr` : '+'}
                    </span>
                    <span className="font-medium">{tier.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  )
}
